import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { color } from './tokens';

type Intensity = 'full' | 'calm';

interface Spark {
    x: number;
    y: number;
    r: number;
    vy: number;
    vx: number;
    phase: number;
    hue: 'spark' | 'signal';
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The subtle moving background behind every screen: drifting colour glows, a faint perspective grid,
 * and a field of slowly rising sparks. Fixed, non-interactive, behind content.
 * `calm` is for working surfaces (boards, retros) where content must stay legible.
 */
export function SparkField({ intensity = 'full' }: { intensity?: Intensity }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const reduced = prefersReducedMotion();
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        let width = 0;
        let height = 0;
        let sparks: Spark[] = [];
        let raf = 0;
        let last = performance.now();
        let pointerX = 0.5;
        let pointerY = 0.5;

        const density = intensity === 'full' ? 1 / 16000 : 1 / 38000;
        const alphaScale = intensity === 'full' ? 1 : 0.55;

        const spawn = (anywhere: boolean): Spark => ({
            x: Math.random() * width,
            y: anywhere ? Math.random() * height : height + 10,
            r: Math.random() * 1.3 + 0.35,
            vy: -(Math.random() * 9 + 4),
            vx: (Math.random() - 0.5) * 4,
            phase: Math.random() * Math.PI * 2,
            hue: Math.random() < 0.3 ? 'spark' : 'signal',
        });

        const resize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const target = Math.max(18, Math.min(140, Math.round(width * height * density)));
            sparks = Array.from({ length: target }, () => spawn(true));
        };

        const draw = (t: number, dt: number) => {
            ctx.clearRect(0, 0, width, height);
            const offsetX = (pointerX - 0.5) * 14;
            const offsetY = (pointerY - 0.5) * 10;
            for (let i = 0; i < sparks.length; i++) {
                const s = sparks[i];
                s.y += s.vy * dt;
                s.x += s.vx * dt + Math.sin(t / 2400 + s.phase) * 0.08;
                if (s.y < -10) sparks[i] = spawn(false);

                const twinkle = 0.55 + 0.45 * Math.sin(t / 900 + s.phase);
                const depth = s.r / 1.65; // bigger sparks feel closer: more parallax
                const x = s.x + offsetX * depth;
                const y = s.y + offsetY * depth;
                const a = twinkle * 0.75 * alphaScale;
                const rgb = s.hue === 'spark' ? '255, 177, 46' : '120, 200, 255';

                const glow = ctx.createRadialGradient(x, y, 0, x, y, s.r * 6);
                glow.addColorStop(0, `rgba(${rgb}, ${a * 0.5})`);
                glow.addColorStop(1, `rgba(${rgb}, 0)`);
                ctx.fillStyle = glow;
                ctx.fillRect(x - s.r * 6, y - s.r * 6, s.r * 12, s.r * 12);

                ctx.fillStyle = `rgba(${rgb}, ${a})`;
                ctx.beginPath();
                ctx.arc(x, y, s.r, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        const loop = (now: number) => {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            draw(now, dt);
            raf = requestAnimationFrame(loop);
        };

        const onVisibility = () => {
            cancelAnimationFrame(raf);
            if (!document.hidden && !reduced) {
                last = performance.now();
                raf = requestAnimationFrame(loop);
            }
        };

        const onPointer = (e: PointerEvent) => {
            pointerX = e.clientX / Math.max(1, width);
            pointerY = e.clientY / Math.max(1, height);
        };

        resize();
        if (reduced) {
            draw(0, 0);
        } else {
            raf = requestAnimationFrame(loop);
            window.addEventListener('pointermove', onPointer, { passive: true });
        }
        window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', onPointer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [intensity]);

    const glowOpacity = intensity === 'full' ? 1 : 0.55;

    return (
        <Box
            aria-hidden
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: -1,
                overflow: 'hidden',
                pointerEvents: 'none',
                backgroundColor: color.void,
                '@keyframes cs-drift-a': {
                    '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
                    '50%': { transform: 'translate3d(6vw, 4vh, 0) scale(1.12)' },
                },
                '@keyframes cs-drift-b': {
                    '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1.05)' },
                    '50%': { transform: 'translate3d(-5vw, -6vh, 0) scale(0.95)' },
                },
                '@keyframes cs-grid-pan': {
                    from: { backgroundPosition: '0 0' },
                    to: { backgroundPosition: '0 64px' },
                },
            }}
        >
            {/* Colour glows */}
            <Box sx={{
                position: 'absolute', width: '70vmax', height: '70vmax', left: '-20vmax', top: '-30vmax',
                background: `radial-gradient(circle, ${color.violet}55, transparent 62%)`,
                filter: 'blur(20px)', opacity: glowOpacity,
                animation: 'cs-drift-a 26s ease-in-out infinite',
            }} />
            <Box sx={{
                position: 'absolute', width: '60vmax', height: '60vmax', right: '-22vmax', top: '10vh',
                background: `radial-gradient(circle, ${color.signal}38, transparent 60%)`,
                filter: 'blur(24px)', opacity: glowOpacity,
                animation: 'cs-drift-b 32s ease-in-out infinite',
            }} />
            <Box sx={{
                position: 'absolute', width: '50vmax', height: '50vmax', left: '20vw', bottom: '-36vmax',
                background: `radial-gradient(circle, ${color.rose}22, transparent 60%)`,
                filter: 'blur(30px)', opacity: glowOpacity,
                animation: 'cs-drift-a 40s ease-in-out infinite reverse',
            }} />

            {/* Faint grid, fading out toward the edges */}
            <Box sx={{
                position: 'absolute', inset: '-64px 0 0 0',
                backgroundImage: `linear-gradient(${color.line} 1px, transparent 1px), linear-gradient(90deg, ${color.line} 1px, transparent 1px)`,
                backgroundSize: '64px 64px',
                opacity: intensity === 'full' ? 0.35 : 0.18,
                maskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, #000 20%, transparent 75%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, #000 20%, transparent 75%)',
                animation: 'cs-grid-pan 12s linear infinite',
            }} />

            <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0 }} />

            {/* Vignette keeps edges deep */}
            <Box sx={{
                position: 'absolute', inset: 0,
                background: `radial-gradient(ellipse at 50% 45%, transparent 55%, ${color.void} 100%)`,
            }} />
        </Box>
    );
}
